<template>
  <div
    class="fill-height d-flex selection-page-wrapper"
  >
    <v-card class="control mr-2 pa-6">
      <div
        class="d-flex justify-end align-center"
        :class="[showHint ? 'mb-6' : '']"
      >
        <v-expand-transition>
          <div
            v-if="showHint"
            class="info-container"
          >
            <v-icon color="primary">
              mdi-alert-circle-outline
            </v-icon>
            <div
              v-show="rasterType === RasterType.TIMESERIES"
              class="ml-4"
            >
              <span
                v-for="(item, index) in timeseriesText"
                :key="index"
              >
                <component
                  :is="item.component"
                  v-if="item.type === 'icon'"
                  class="inline-block ml-1"
                />
                <span v-else>{{ item.content }}</span>
              </span>
            </div>
            <div
              v-show="rasterType === RasterType.GRID"
              class="ml-4"
            >
              <span
                v-for="(item, index) in gridText"
                :key="index"
              >
                <br v-if="item.type === 'br'">
                <component
                  :is="item.component"
                  v-else-if="item.type === 'icon'"
                  class="inline-block ml-1"
                />
                <span v-else>{{ item.content }}</span>
              </span>
            </div>
            <v-spacer />
            <v-btn
              icon
              size="32"
              variant="text"
              @click="removeHint"
            >
              <v-icon color="scrim">
                mdi-close-circle
              </v-icon>
            </v-btn>
          </div>
        </v-expand-transition>
      </div>
      <div class="d-flex justify-space-between mb-2">
        <h3>{{ title }}</h3>
        <v-btn-toggle
          v-model="rasterType"
          density="compact"
          mandatory
          color="primary"
          class="mb-4"
        >
          <v-tooltip
            :text="t('raster.tooltip.switch_mode_to_timeseries')"
            bottom
            :open-delay="200"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                data-testid="timeseries-mode"
                variant="outlined"
              >
                {{
                  t('timeseries')
                }}
              </v-btn>
            </template>
          </v-tooltip>
          <v-tooltip
            :text="t('raster.tooltip.switch_mode_to_bounding_box')"
            bottom
            :open-delay="200"
            style="z-index: 99999"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                data-testid="raster-mode"
                variant="outlined"
              >
                {{
                  t('bounding_box')
                }}
              </v-btn>
            </template>
          </v-tooltip>
        </v-btn-toggle>
      </div>
      <timeseries-control-panel
        v-show-important="rasterType === RasterType.TIMESERIES"
      />
      <grid-control-panel v-show-important="rasterType === RasterType.GRID" />
    </v-card>
    <v-card class="ml-2 map">
      <!-- pass visible prop to maps such that they can reload the tiles at v-show=true -->
      <timeseries-map
        v-show="rasterType === RasterType.TIMESERIES"
        :key="'timeseries-' + rasterType"
        data-testid="timeseries-map"
        class="full-height-width"
        :visible="rasterType === RasterType.TIMESERIES"
      />
      <grid-map
        v-show="rasterType === RasterType.GRID"
        :key="'grid-' + rasterType"
        data-testid="grid-map"
        class="full-height-width"
        :visible="rasterType === RasterType.GRID"
      />
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import ParameterStore from '@/stores/parameter/parameter-module';
import DatasetStore from '@/stores/dataset/dataset-module';
import TimeseriesStore from '@/stores/timeseries/timeseries-module';
import TimeseriesMap from '@/components/raster/TimeseriesMap.vue';
import GridMap from '@/components/raster/GridMap.vue';
import GridControlPanel from '@/components/raster/GridControlPanel.vue';
import TimeseriesControlPanel from '@/components/raster/TimeseriesControlPanel.vue';
import PlusIcon from '@/components/icons/PlusIcon.vue';
import MarkerIcon from '@/components/icons/MarkerIcon.vue';
import RectangleBoxIcon from '@/components/icons/RectangleBoxIcon.vue';
import { useI18n } from "vue-i18n";

export enum RasterType {
  TIMESERIES = 0,
  GRID = 1,
}

export default defineComponent({
  name: 'RasterSelectionPage',
  components: {
    GridMap,
    TimeseriesMap,
    GridControlPanel,
    TimeseriesControlPanel,
  },
  props: {
    defaultType: {
      type: String,
      required: true,
    },
  },
  emits: ['onTitleChange', 'onRasterTypeChanged'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const rasterType = ref<RasterType>(RasterType.TIMESERIES);
    const RasterTypeEnum = RasterType;
    const showHint = ref(true);

    const parameterStore = ParameterStore;
    const datasetStore = DatasetStore;

    if (props.defaultType === 'grid') {
      rasterType.value = RasterType.GRID;
    } else {
      rasterType.value = RasterType.TIMESERIES;
    }

    const title = computed(() => {
      switch (rasterType.value) {
        case RasterType.TIMESERIES:
          return (
            t('raster.timeseries_title') +
            ' (' +
            Object.keys(TimeseriesStore.activeCoords).length +
            ')'
          );
        case RasterType.GRID:
          return t('raster.grid_title').toString();
        default:
          return '';
      }
    });

    const timeseriesText = computed(() => {
      const rawString = t('raster.info_text.timeseries', {
        grid_marker_html: '{grid_marker_html}',
        plus_marker_html: '{plus_marker_html}'
      });
      return rawString.split(/(\{grid_marker_html\}|\{plus_marker_html\})/).map(part => {
        if (part === '{grid_marker_html}') return { type: 'icon', component: MarkerIcon };
        if (part === '{plus_marker_html}') return { type: 'icon', component: PlusIcon };
        return { type: 'text', content: part };
      });
    });

    const gridText = computed(() => {
      const rawString = t('raster.info_text.grid', {
        grid_box_html: '{grid_box_html}'
      });
      return rawString.split(/(\{grid_box_html\}|\n)/).map(part => {
        if (part === '{grid_box_html}') return { type: 'icon', component: RectangleBoxIcon };
        if (part === '\n') return { type: 'br' };
        return { type: 'text', content: part };
      });
    });

    watch(
      title,
      () => emit('onTitleChange', title.value),
      { immediate: true }
    );
    watch(rasterType, () => emit('onRasterTypeChanged', rasterType.value));

    const removeHint = () => {
      showHint.value = false;
    };

    return {
      t,
      rasterType,
      RasterType: RasterTypeEnum,
      showHint,
      title,
      removeHint,
      parameterStore,
      datasetStore,
      timeseriesText,
      gridText,
    };
  },
});
</script>

<style scoped>
.selection-page-wrapper {
  max-height: 100%;
}
.control {
  display: flex;
  flex-direction: column;
  min-width: 700px;
  max-width: 900px;
  width: 40%;
}

.full-height-width {
  width: 100%;
  height: 100%;
}

.map {
  min-width: 500px;
  width: 60%;
  height: 100%;
  z-index: 9;
}

.info-container {
  width: calc(100% + 24px + 24px);
  display: flex;
  align-items: center;
  background: rgb(var(--v-container-base));
  margin: -24px -24px 24px -24px;
  padding: 0 16px 0 16px;
  border-top: 1px solid rgb(var(--v-theme-primary));
  border-bottom: 1px solid rgb(var(--v-theme-primary));
  border-radius: 5px 5px 0 0;
}
</style>
